/**
 * ChatArea用フック
 * チャットメッセージ、ドラッグ&ドロップなどの状態管理を統合
 */

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AgeGroup } from '@/constants';
import { useChat } from '@/hooks/useChat';
import { useDragAndDrop } from '@/hooks/useDragAndDrop';
import { DEFAULT_MAX_FILES } from '@/services/fileService';
import type { PromptInputRef } from '@/components/input/PromptInput';

interface UseChatAreaProps {
  sessionId: string;
  ageGroup: AgeGroup;
  onMessageSent?: () => void;
}

export const useChatArea = ({ sessionId, ageGroup, onMessageSent }: UseChatAreaProps) => {
  const { t } = useTranslation();
  const {
    messages,
    addMessage,
    isLoading,
    isLoadingSession,
    chatUsage,
    startUploading,
    completeStreaming,
  } = useChat(sessionId, ageGroup);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<PromptInputRef>(null);
  const requestScrollToEndRef = useRef<boolean>(false);

  // ドラッグ&ドロップフック
  const { isDragging, droppedFiles, dropError, handlers, clearDroppedFiles, setDropError } =
    useDragAndDrop({
      maxFiles: DEFAULT_MAX_FILES,
    });

  // ファイル添付エラーをdropErrorと統合して表示
  const handleFileError = useCallback(
    (error: string) => {
      setDropError(error);
    },
    [setDropError]
  );

  // メッセージ送信時のハンドラー
  const handleSendMessage = useCallback(
    async (text: string, attachedFileIds?: string[]) => {
      requestScrollToEndRef.current = true;
      await addMessage(text, attachedFileIds);
      // メッセージ送信後にチャット履歴を更新
      if (onMessageSent) {
        onMessageSent();
      }
    },
    [addMessage, onMessageSent]
  );

  const handleStartUploading = useCallback(
    (...args: Parameters<typeof startUploading>) => {
      requestScrollToEndRef.current = true;
      startUploading(...args);
    },
    [startUploading]
  );

  // 自動スクロール
  useEffect(() => {
    if (!requestScrollToEndRef.current) return;
    requestScrollToEndRef.current = false;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // セッション切り替え時に入力欄にフォーカス
  useEffect(() => {
    chatInputRef.current?.focus();
  }, [sessionId]);

  // ウェルカムメッセージ（useMemoで最適化）
  const welcomeMsg = useMemo(
    () => ({
      greeting: t('chat.welcome.greeting'),
      subtitle: t('chat.welcome.subtitle'),
      examplesTitle: t('chat.welcome.examplesTitle'),
      examples: Object.values(t('chat.welcome.examples', { returnObjects: true })) as string[],
    }),
    [t]
  );

  return {
    // 状態
    messages,
    isLoading,
    isLoadingSession,
    chatUsage,
    isDragging,
    droppedFiles,
    dropError,
    welcomeMsg,

    // refs
    messagesEndRef,
    chatInputRef,

    // ハンドラー
    handleSendMessage,
    startUploading: handleStartUploading,
    clearDroppedFiles,
    handleFileError,
    handlers,
    onStreamingComplete: completeStreaming,
  };
};
